import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Input, LanguageMenu, ThemeMenu } from '@easydent/shared-ui';
import { useAuthStore } from '@/stores/authStore';
import { login } from '@/lib/api';

const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const s = await login(email.trim(), password);
      setSession(s.accessToken, s.name);
      navigate('/', { replace: true });
    } catch {
      setError(t('login.badCredentials', 'Invalid email or password.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-sm rounded-2xl border border-line bg-surface p-8 shadow-sm">
        <h1 className="text-center text-2xl font-bold text-brand-ink-strong">easyDent</h1>
        <p className="mt-1 text-center text-sm text-muted">{t('loginSubtitle', 'Advertiser portal')}</p>
        <div className="mt-3 flex justify-center">
          <LanguageMenu
            value={i18n.language}
            languages={[{ code: 'ro', label: 'Română' }, { code: 'en', label: 'English' }]}
            onChange={(code) => i18n.changeLanguage(code)}
            aria-label={t('language', 'Language')}
          />
          <ThemeMenu labels={{ label: t('theme.label', 'Theme'), light: t('theme.light', 'Light'), dark: t('theme.dark', 'Dark'), system: t('theme.system', 'System') }} />
        </div>
        {error && <Alert variant="error" className="mt-4">{error}</Alert>}
        <form onSubmit={submit} className="mt-6 space-y-3">
          <Input type="email" placeholder={t('email', 'Email')} value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Input type="password" placeholder={t('password', 'Password')} value={password} onChange={(e) => setPassword(e.target.value)} required />
          <Button className="w-full" loading={busy} disabled={!email.trim() || !password}>{t('signIn', 'Sign in')}</Button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
